import {
  Box,
  Chip,
  Rating,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { constantCase } from 'case-anything';
import dayjs from 'dayjs';
import { ReadOnly } from '@ui-framework/index';
import { IconChip } from './PageSection/index';
import IconLibrary from './IconLibrary';
import { useAvailabilitySlotTypes } from '../providers/AvailabilitySlotTypeProvider';

export const SLOT_TYPE_CHIP = {
  ALL_DAY: {
    bg: '#B2DFDB',
    fg: '#004D40',
    hover: '#80CBC4',
  },
  MORNING: { bg: '#BBDEFB', fg: '#0D47A1', hover: '#90CAF9' },
  AFTERNOON: {
    bg: '#FFE0B2',
    fg: '#BF360C',
    hover: '#FFCC80',
  },
  EVENING: {
    bg: '#E1BEE7',
    fg: '#4A148C',
    hover: '#CE93D8',
  },
};

function formatTime({ after = null, before = null }) {
  const afterHour = after ? dayjs(after, 'HH:mm').format('hh:mm A') : '';
  const beforeHour = before ? dayjs(before, 'HH:mm').format('hh:mm A') : '';
  if (afterHour && beforeHour) {
    return `Between ${afterHour} - ${beforeHour}`;
  }
  if (afterHour) {
    return `After ${afterHour}`;
  }
  if (beforeHour) {
    return `Before ${beforeHour}`;
  }
  return null;
}

export function getSlotChipSx({ slotKey, disabled = false, theme }) {
  const c = SLOT_TYPE_CHIP[slotKey] || SLOT_TYPE_CHIP.MORNING;
  if (disabled) {
    return {
      bgcolor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      '&:hover': {
        bgcolor: theme.palette.action.disabledBackground,
      },
    };
  }
  return {
    bgcolor: c.bg,
    color: c.fg,
    '&:hover': { bgcolor: c.hover },
  };
}

function CustomChipLabel({ name, type, showScore = false, score = 0 }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography component="span" variant="subtitle">
        <FormattedMessage id={constantCase(name)} defaultMessage={name} />
      </Typography>
      {type === '+SS' && (
        <Tooltip
          arrow
          placement="top"
          title={
            <FormattedMessage
              id="THIS_SLOT_WAS_ADDED_BY_USER_ON_LIVE_CALENDAR"
              defaultMessage="This slot was added by user on live calendar"
            />
          }>
          <Box>
            <IconChip
              sx={{ pointerEvents: 'auto' }}
              size={15}
              iconFontSize={10}
              Icon={IconLibrary.SpecialSchedule}
              bgColor="primary.dark"
              iconColor="white"
            />
          </Box>
        </Tooltip>
      )}
      {showScore && (
        <Rating
          value={Number(score)}
          readOnly
          size="small"
          sx={{
            '& .MuiRating-iconEmpty': {
              display: 'none',
            },
            '& .MuiRating-iconFilled svg': {
              color: '#FFC107',
              stroke: '#8c742c',
              strokeWidth: 0.8,
            },
          }}
        />
      )}
    </Box>
  );
}

function TimeSlotTypeChip({
  name,
  code,
  type = '',
  onClick = null,
  disabled = false,
  showScore = false,
  score = 0,
  ...props
}) {
  const theme = useTheme();
  return (
    <Chip
      {...(onClick && {
        onClick: e =>
          onClick(
            {
              name,
              code,
            },
            e,
          ),
      })}
      label={
        <CustomChipLabel
          name={name}
          type={type}
          showScore={showScore}
          score={score}
        />
      }
      size="small"
      variant="filled"
      sx={{
        ...getSlotChipSx({ slotKey: code, disabled, theme }),
        justifyContent: 'flex-start',
        '& .MuiChip-label': {
          width: '100%',
          textAlign: 'left',
        },
        minWidth: '80px',
        ...(props?.sx || {}),
      }}
    />
  );
}

export function TimeSlotTypeChipWithAfterBeforeHours({
  name,
  code,
  afterHour = null,
  beforeHour = null,
  smallAfterAndBefore = false,
  direction = 'row',
}) {
  let nameFound = name;
  const { slotTypes } = useAvailabilitySlotTypes();
  if (!nameFound) {
    nameFound = slotTypes.find(i => i?.code === code)?.name;
  }

  const isColumnDirection = direction === 'column';
  return (
    <Stack
      spacing={1}
      direction={direction}
      sx={{
        display: 'flex',
        width: 'fit-content',
        alignItems: isColumnDirection ? 'left' : 'center',
      }}>
      {code && (
        <TimeSlotTypeChip
          sx={{
            justifyContent: 'center',
            width: 'fit-content',
            minWidth: 0,
            '& .MuiChip-label': { textAlign: 'center' },
          }}
          name={nameFound}
          code={code}
        />
      )}
      {(beforeHour || afterHour) && (
        <ReadOnly
          sx={{
            ...(smallAfterAndBefore && {
              fontSize: '12px',
            }),
          }}>
          {formatTime({
            after: afterHour,
            before: beforeHour,
          })}
        </ReadOnly>
      )}
      {!code && !beforeHour && !afterHour && (
        <ReadOnly>
          <FormattedMessage
            id="NO_SLOT_SELECTED"
            defaultMessage="No slot selected"
          />
        </ReadOnly>
      )}
    </Stack>
  );
}

export default TimeSlotTypeChip;
